import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';

import {catchError, map} from 'rxjs/operators';
import * as flat from 'flat';

@Injectable({
  providedIn: 'root'
})
export class TypesenseService {

  private httpClient: HttpClient;
  private meiliSearchBasePath = environment.meiliSearch;

  constructor(
    private handler: HttpBackend,
  ) {
    this.httpClient = new HttpClient(this.handler);
  }

  /**
   * Retrieves a specified collection from the Typesense API.
   *
   * @template T
   * @param collectionName - The name of the collection to retrieve
   * @param [options={}] - Additional options to be passed in the GET request
   * @returns Returns an observable of type T.
   * The observable resolves with the data from the specified collection.
   * The data is obtained from the Typesense API using the httpClient.
   * @throws {HttpErrorResponse} If the request fails, the function will throw an error.
   */
  getCollection<T>(collectionName: string, options: object = {}): Observable<T> {
    return this.httpClient.get<T>(`${this.meiliSearchBasePath}/${collectionName}`, {
      headers: new HttpHeaders({
        'X-TYPESENSE-API-KEY': environment.typeSense.apiKey
      }),
      ...options
    })
      .pipe(
        map((data: any) => data.hits && data.hits.length ? data.hits.map((item: any) => flat.unflatten(item.document)) : []),
        catchError(this.formatErrors));
  }

  /**
   * Search the specified collection from the Typesense API
   *
   * @template T
   * @param collectionName - The name of the collection to search
   * @param [options={}] - Additional options to be passed in the GET request
   * @returns Returns an observable of type T.
   * The observable resolves with the search data from the specified collection.
   * The data is obtained from the Typesense API using the httpClient.
   * @throws {HttpErrorResponse} If the request fails, the function will throw an error.
   */
  search<T>(collectionName: string, body: object = {}): Observable<T> {
    return this.httpClient.post<T>(`${this.meiliSearchBasePath}/indexes/${collectionName}/search`, {...body},{
      headers: new HttpHeaders({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: `Bearer ${environment.meiliSearchKey}`
      }),
    })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * Retrieves facet counts from the specified collection from the Typesense API
   *
   *
   * @template T
   * @param collectionName - The name of the collection to retrieve facets from
   * @param [options={}] - Additional options to be passed in the GET request
   * @returns Returns an observable of type T.
   * The observable resolves with the facet counts from the specified collection.
   * The data is obtained from the Typesense API using the httpClient.
   * @throws {HttpErrorResponse} If the request fails, the function will throw an error.
   */
  getFacets<T>(collectionName: string, options: object = {}): Observable<T> {
    return this.httpClient.get<T>(`${this.meiliSearchBasePath}/indexes/${collectionName}/search`, {
      headers: new HttpHeaders({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: `Bearer ${environment.typeSense.apiKey}`
      }),
      ...options
    })
      .pipe(
        map((data: any) => data.facet_counts && data.facet_counts.length ? data.facet_counts : []),
        catchError(this.formatErrors));
  }

  /**
   * Transforms the hits returned by the Typesense API by unflattening the documents.
   *
   * @param hits - The hits to transform
   * @returns The transformed hits with unflattened documents
   */
  private getHits(hits: any[]) {
    if (hits && hits.length) {
      return hits.map((item: any) => {
        return {
          ...item,
          document: flat.unflatten(item.document)
        };
      });
    }
    return [];
  }

  /**
   * Formats error messages thrown by the httpClient.
   *
   * @param error - The error message to format
   * @returns An observable that throws the formatted error message.
   */
  private formatErrors(error: any): Observable<any> {
    return throwError(() => error.error);
  }
}
